import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type {
  AmountDifferenceType,
  AssessmentsAccumulationMappingResponse,
} from '@/entities/assessments/api/generated/model';
import { getAmountDifferenceTypeLabel } from '@/entities/assessments/lib/getAmountDifferenceTypeLabel';
import type { AmountUnit } from '@/shared/lib/amount/convertAmount';
import { convertAmount } from '@/shared/lib/amount/convertAmount';
import { formatAmount, HIERARCHY_SEPARATOR } from '@/shared/lib/format';
import { Status, type StatusVariant, Text } from '@/shared/ui';
import { ASSESSMENT_TABLE_NAME_COLUMN_WIDTH } from './assessment-table-columns';

type AIAccumulationsTableColumnsProps = {
  /** 基準年度 */
  baseFiscalYear: number;
  /** 比較年度 */
  comparedFiscalYear: number;
  /** 金額単位 */
  amountUnit: AmountUnit;
  /** 文言の翻訳 */
  t: TFunction<'translation'>;
};

const STATUS_COLUMN_WIDTH = 95;
const NAME_COLUMN_WIDTH = ASSESSMENT_TABLE_NAME_COLUMN_WIDTH - STATUS_COLUMN_WIDTH;
const AMOUNT_COLUMN_MIN_WIDTH = 160;

/** 状態に応じたStatusバリアントを返す */
const getStatusVariant = (type: AmountDifferenceType): StatusVariant => {
  switch (type) {
    case 'increased':
    case 'new':
      return 'error';
    case 'decreased':
    case 'deleted':
      return 'success';
    default:
      return 'neutral';
  }
};

/** 状態に応じた差額の色を返す */
const getDifferenceColor = (type: AmountDifferenceType): 'base' | 'success' | 'error' => {
  switch (type) {
    case 'increased':
    case 'new':
      return 'error';
    case 'decreased':
    case 'deleted':
      return 'success';
    default:
      return 'base';
  }
};

/** 差額の表示テキストを生成 */
const formatDifferenceAmount = ({
  amount,
  type,
  amountUnit,
  t,
}: {
  amount: number;
  type: AmountDifferenceType;
  amountUnit: AmountUnit;
  t: TFunction<'translation'>;
}): string => {
  const convertedAmount = convertAmount(Math.abs(amount), amountUnit) ?? 0;
  const unitKey =
    amountUnit === 'thousand' ? 'common.unit.amount-thousand-yen' : 'common.unit.amount-yen';
  const formattedAmount = t(unitKey, { amount: formatAmount(convertedAmount) });
  if (type === 'unchanged') {
    return formattedAmount;
  }
  if (type === 'increased' || type === 'new') {
    return `+ ${formattedAmount}`;
  }
  return `- ${formattedAmount}`;
};

/**
 * AI積算明細テーブルのカラム定義を生成
 */
export const createAIAccumulationsTableColumns = ({
  baseFiscalYear,
  comparedFiscalYear,
  amountUnit,
  t,
}: AIAccumulationsTableColumnsProps): ColumnDef<AssessmentsAccumulationMappingResponse>[] => {
  return [
    {
      id: 'status',
      header: t('assessment.comparison-table.fields.status'),
      meta: { width: STATUS_COLUMN_WIDTH },
      cell: ({ row }) => {
        const type = row.original.amount_difference_type;
        return (
          <Status variant={getStatusVariant(type)} className="font-medium text-xs">
            {getAmountDifferenceTypeLabel({ type, t })}
          </Status>
        );
      },
    },
    {
      id: 'name',
      header: t('assessment.comparison-table.fields.name'),
      meta: { width: NAME_COLUMN_WIDTH },
      cell: ({ row }) => {
        const target = [row.original.base_description, row.original.compared_description]
          .filter(Boolean)
          .join(HIERARCHY_SEPARATOR);
        return <Text>{target}</Text>;
      },
    },
    {
      id: 'base_amount',
      header: t('assessment.comparison-table.budget-amount', { year: baseFiscalYear }),
      meta: { width: 'auto', minWidth: AMOUNT_COLUMN_MIN_WIDTH },
      cell: ({ row }) => {
        return (
          <Text viewMode="number" unit={amountUnit} className="w-full text-right">
            {row.original.base_total_amount}
          </Text>
        );
      },
    },
    {
      id: 'compared_amount',
      header: t('assessment.comparison-table.budget-amount', { year: comparedFiscalYear }),
      meta: { width: 'auto', minWidth: AMOUNT_COLUMN_MIN_WIDTH },
      cell: ({ row }) => {
        return (
          <Text viewMode="number" unit={amountUnit} className="w-full text-right">
            {row.original.compared_total_amount}
          </Text>
        );
      },
    },
    {
      id: 'difference',
      header: t('assessment.fields.difference'),
      meta: { width: 'auto', minWidth: AMOUNT_COLUMN_MIN_WIDTH },
      cell: ({ row }) => {
        const type = row.original.amount_difference_type;
        const amount = row.original.difference_amount;
        const formattedAmount = formatDifferenceAmount({ amount, type, amountUnit, t });
        return (
          <Text color={getDifferenceColor(type)} className="w-full text-right">
            {formattedAmount}
          </Text>
        );
      },
    },
  ];
};
